import { UpgradeSubscriptionModal } from '@components'

import {
  ButtonDropdown,
  IconButton,
  useModal,
  useModalMultiple,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { EditManualBankAccountModal } from '@features/bankAccounts/elements/EditManualBankAccountModal'
import { EditManualBankAccountModalState } from '@features/bankAccounts/elements/EditManualBankAccountModal/enums/editManualBankAccountModalState'
import { useDownloadBankLines } from '@features/bankAccounts/hooks/useDownloadBankLines'
import { useRedirectToCsvImport } from '@features/bankAccounts/hooks/useRedirectToCsvImport'
import { useBankConnectionLimit } from '@features/openBanking/hooks/useBankConnectionLimit'
import { useInitiateOpenBankingConnection } from '@features/openBanking/hooks/useInitiateOpenBankingConnection'
import { UiSource } from '@features/reconciliation/enums'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { TrackingContext } from '../../../../../../../../../../enums/trackingContext'
import { useComponentId, useSegment } from '../../../../../../../../../../hooks'
import { EditConnectedBankAccountModal } from '../../../../../EditConnectedBankAccountModal/EditConnectedBankAccountModal'
import { CardActionType } from '../../../../enums/cardActionType'
import { getCardActionItems } from '../../../../utils/getCardActionItems'
import { HideAccountModal } from '../HideAccountModal'

interface CardActionsButtonProps {
  accountId?: string
  bankAccountId?: string
  actions: CardActionType[]
}

const EditManualBankAccountModalRender = withModalConditionalRender(EditManualBankAccountModal)
const EditConnectedBankAccountModalRender = withModalConditionalRender(EditConnectedBankAccountModal)

export const CardActionsButton = ({
  accountId,
  bankAccountId,
  actions,
}: CardActionsButtonProps): ReactElement | null => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { connectToOpenBanking } = useInitiateOpenBankingConnection(UiSource.AccountPageCta)
  const editManualBankAccountModalId = useComponentId(ModalId.EditManualBankAccountModal)
  const editConnectedBankAccountModalId = useComponentId(ModalId.EditConnectedBankAccountModal)
  const hideModalId = useComponentId(ModalId.HideAccountModal)
  const { open: openModal } = useModalMultiple([
    ModalId.BankConnectionRemoveConfirmationModal,
    hideModalId,
    editManualBankAccountModalId,
    editConnectedBankAccountModalId,
  ])
  const { redirectToCsvImport } = useRedirectToCsvImport()
  const { download: downloadBankLines } = useDownloadBankLines()

  const { isLimitReached } = useBankConnectionLimit()
  const upgradeModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { open: openUpgradeModal } = useModal(upgradeModalId)

  const items = useMemo(() => getCardActionItems(actions, t), [actions, t])

  const handleActionSelect = useCallback(
    (_id: string, item: CardActionType) => {
      switch (item) {
        case CardActionType.ConnectToBank:
          if (isLimitReached) {
            openUpgradeModal()
            break
          }
          connectToOpenBanking()
          track('XXX - Reconciliation Bank Connection Initiated (Client)')
          break
        case CardActionType.RemoveConnection:
          openModal(ModalId.BankConnectionRemoveConfirmationModal)
          break
        case CardActionType.UploadToAccount:
          redirectToCsvImport(accountId ?? '')
          break
        case CardActionType.DownloadTransactions:
          if (accountId) {
            downloadBankLines(accountId)
          }
          break
        case CardActionType.HideAccount:
          openModal(hideModalId)
          break
        case CardActionType.EditAccount:
          openModal(editManualBankAccountModalId)
          break
        case CardActionType.EditAccountName:
          openModal(editConnectedBankAccountModalId)
          break
        default:
          break
      }
    },
    [
      connectToOpenBanking,
      openModal,
      isLimitReached,
      openUpgradeModal,
      track,
      redirectToCsvImport,
      downloadBankLines,
      accountId,
      hideModalId,
      editManualBankAccountModalId,
      editConnectedBankAccountModalId,
    ],
  )

  if (items.length === 0) {
    return null
  }

  return (
    <>
      <ButtonDropdown
        items={items}
        onSelect={handleActionSelect}
        placement="bottom-end"
        triggerElement={
          <IconButton aria-label={t('bank.accounts.list.card.actions.trigger')} icon="ellipsis" variant="outlined" />
        }
      />
      <UpgradeSubscriptionModal id={upgradeModalId} trackingContext={TrackingContext.BankConnectionLimitsExceeded} />
      <HideAccountModal accountId={accountId} id={hideModalId} />
      <EditManualBankAccountModalRender
        id={editManualBankAccountModalId}
        accountId={accountId}
        bankAccountId={bankAccountId}
        state={EditManualBankAccountModalState.Edit}
      />
      {bankAccountId && (
        <EditConnectedBankAccountModalRender id={editConnectedBankAccountModalId} bankAccountId={bankAccountId} />
      )}
    </>
  )
}
